import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { History, Loader2, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTION_COLOR = {
  created: 'text-emerald-400',
  updated: 'text-sky-400',
  saved: 'text-sky-400',
  archived: 'text-amber-400',
  restored: 'text-teal-400',
};

export const ActivityLogPanel = ({ orgId, isDark = true }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/audit-log/${orgId}?limit=200`);
      setEntries(r.data.entries || []);
    } catch (e) {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const text = isDark ? 'text-white' : 'text-slate-900';
  const sub = isDark ? 'text-slate-400' : 'text-slate-500';
  const card = isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200';

  const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="space-y-4" data-testid="activity-log-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-lg font-bold ${text} flex items-center gap-2`}>
            <History className="h-5 w-5 text-amber-500" /> Activity Log
          </h2>
          <p className={`text-sm ${sub}`}>Append-only record of admin changes (who did what, when).</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-xs border-slate-600" onClick={load} data-testid="activity-refresh">
          <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className={`flex items-center gap-2 ${sub} py-10 justify-center`}><Loader2 className="h-5 w-5 animate-spin" /> Loading activity…</div>
      ) : entries.length === 0 ? (
        <div className={`border rounded-lg p-8 text-center ${card}`}><p className={sub}>No activity recorded yet.</p></div>
      ) : (
        <div className={`border rounded-lg divide-y ${isDark ? 'divide-slate-700 border-slate-700' : 'divide-slate-200 border-slate-200'}`}>
          {entries.map((e) => (
            <div key={e.id} className="flex items-start gap-3 px-3 py-2 text-sm" data-testid={`activity-row-${e.id}`}>
              <span className={`font-semibold w-20 shrink-0 ${ACTION_COLOR[e.action] || sub}`}>{e.action}</span>
              <span className={`${text} flex-1`}>
                <span className="opacity-80">{e.entity_type.replace(/_/g, ' ')}</span>
                {e.summary && <span className={sub}> — {e.summary}</span>}
              </span>
              <span className={`${sub} shrink-0 text-xs text-right`}>
                {e.actor_name || 'system'}<br /><span className="opacity-70">{fmt(e.created_at)}</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActivityLogPanel;
